import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { ArrowLeft, ArrowRight, Check, MapPin } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { setTourState } from '../../lib/tutorial';

// Plain-English walkthrough of the existing features. Modal-based (robust +
// mobile-friendly) rather than element-anchored across routes.
const STEPS = [
  { title: 'Welcome to RanchWorks',
    body: 'A quick tour of how things work. It takes about a minute. You can skip anytime.' },
  { title: 'Your dashboard',
    body: 'This is your home base — today\u2019s jobs, money owed, and quick actions all live here.' },
  { title: 'Today screen (Field View)',
    body: 'This is your work list for the day. Open it from your phone in the field — it even works with no signal.',
    to: '/dashboard/field', cta: 'Show me Field View' },
  { title: 'Job cards',
    body: 'Each card is one appointment: who, where, which animals, and your private notes. Private notes are only for you.' },
  { title: 'Job status buttons',
    body: 'Tap "Mark Done" when a job is finished, or "No Show" if the client wasn\u2019t there.' },
  { title: 'Needs Invoice',
    body: 'Tap "Needs Invoice" when the job is done but you still need to bill the client. It adds the job to your billing list.' },
  { title: 'Create Invoice',
    body: 'Use "Create Invoice" to turn a job into a professional invoice with your logo and payment instructions.' },
  { title: 'Payment summary',
    body: 'See at a glance who has paid, who still owes, and what needs an invoice. Tap any box to see the list.' },
  { title: 'Voice Notes',
    body: 'See the orange mic button (bottom-right)? Tap it to speak or type a quick note — it turns to text right on your phone, free. Then save it to a client or make it a reminder, all in one tap.',
    to: '/dashboard/notes', cta: 'Open Voice Notes' },
  { title: 'Clients',
    body: 'Every client you book is saved here with their history, animals, and your private notes.' },
  { title: 'Animals',
    body: 'Clients can save their animals once and reuse them. You\u2019ll see handling and behavior notes on the job.' },
  { title: 'Services',
    body: 'List what you offer and your prices. Clients pick a service when they book.',
    to: '/dashboard/services', cta: 'Open Services' },
  { title: 'Availability',
    body: 'Set the days and hours you work so clients only book open times.',
    to: '/dashboard/availability', cta: 'Open Availability' },
  { title: 'Invoice Settings',
    body: 'Add your business name, logo, and how clients pay you (Venmo, check, etc.). This shows on every invoice.',
    to: '/dashboard/invoice-settings', cta: 'Open Invoice Settings' },
  { title: 'You\u2019re all set',
    body: 'That\u2019s the basics. You can reopen this tour anytime from your dashboard or Settings.' },
];

export default function ProductTour({ open, onOpenChange }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [i, setI] = React.useState(0);

  React.useEffect(() => {
    if (open) setI(0);
  }, [open]);

  const step = STEPS[i];
  const isFirst = i === 0;
  const isLast = i === STEPS.length - 1;

  const close = (patch) => {
    setTourState(user?.id, { lastSeenAt: new Date().toISOString(), ...patch });
    onOpenChange(false);
  };

  const finish = () => close({ completed: true });
  const skip = () => close({ skipped: true });

  const goToPage = () => {
    if (step.to) {
      close({});
      navigate(step.to);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) skip(); }}>
      <DialogContent className="max-w-md" data-testid="product-tour">
        <DialogHeader>
          <DialogTitle data-testid="tour-step-title">{step.title}</DialogTitle>
          <DialogDescription data-testid="tour-step-body">{step.body}</DialogDescription>
        </DialogHeader>

        {step.to && (
          <Button variant="outline" size="sm" onClick={goToPage} data-testid="tour-goto-btn" className="w-fit">
            <MapPin className="h-4 w-4 mr-1" /> {step.cta || 'Take me there'}
          </Button>
        )}

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-1.5 py-1" data-testid="tour-progress">
          {STEPS.map((_, idx) => (
            <span
              key={idx}
              className={`h-1.5 rounded-full transition-all ${idx === i ? 'w-4 bg-primary' : 'w-1.5 bg-muted-foreground/30'}`}
            />
          ))}
        </div>

        <DialogFooter className="flex-row justify-between gap-2 sm:justify-between">
          <Button variant="ghost" size="sm" onClick={skip} data-testid="tour-skip-btn">
            Skip tour
          </Button>
          <div className="flex gap-2">
            {!isFirst && (
              <Button variant="outline" size="sm" onClick={() => setI((n) => n - 1)} data-testid="tour-back-btn">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
            )}
            {isLast ? (
              <Button size="sm" onClick={finish} data-testid="tour-finish-btn">
                <Check className="h-4 w-4 mr-1" /> Finish
              </Button>
            ) : (
              <Button size="sm" onClick={() => setI((n) => n + 1)} data-testid="tour-next-btn">
                Next <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
